/* eslint-disable max-lines */
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import usePageIndex from "../../hooks/usePageIndex";
import { useSearch } from "../../hooks/useSearch";
import { IVoucherCode } from "../../interfaces/endpoints/IVoucherCode";
import { IEsimPackage } from "../../interfaces/esim/EsimPackages.types";
import { getPackageById } from "../../services/Esim.service";
import {
  deleteVoucherCode,
  getAllVoucherCodes
} from "../../services/VoucherCode.service";
import AuditAuthor from "../audit-author/AuditAuthor";
import IconBtn from "../ui/IconBtn";
import Table, { Row } from "../ui/table";
import Can from "../users/permissions/Can";
import DeleteVoucherCode from "./DeleteVoucherCodeModal";

const rewardTypeLabel = (type: number) => {
  switch (type) {
    case 1:
      return "eSIM";
    case 2:
      return "Credits";
    default:
      return "-";
  }
};

const usageModeLabel = (mode: number) => {
  switch (mode) {
    case 1:
      return "Campaign";
    case 2:
      return "EmailLocked";
    default:
      return "-";
  }
};

const formatGB = (gb: number, isUnlimited: boolean) => {
  if (isUnlimited) return "Unlimited";
  return gb >= 1 ? `${gb} GB` : `${Math.round(gb * 1024)} MB`;
};

const columns: (string | number)[] = [
  "Kodi",
  "Lloji",
  "Menyra",
  "Paketa / Krediti",
  "Email (locked)",
  "Skadon",
  "Aktiv",
  "Perdorime",
  "Audit",
  "Veprimet"
];

const VoucherCodes = () => {
  const { response, loading, setResponse } = useSearch(getAllVoucherCodes, {
    withQuerySearch: true
  });
  const { pageIndex } = usePageIndex();
  const [packageMap, setPackageMap] = useState<Record<number, IEsimPackage>>(
    {}
  );

  // Resolve packageTemplateIds via getPackageById
  useEffect(() => {
    if (!response?.data?.length) return;

    const ids = response.data
      .filter(
        (v: IVoucherCode) =>
          v.rewardType === 1 &&
          v.packageTemplateId !== null && v.packageTemplateId !== undefined &&
          !packageMap[v.packageTemplateId]
      )
      .map((v: IVoucherCode) => v.packageTemplateId as number);

    // Deduplicate
    const uniqueIds = Array.from(new Set(ids));
    if (uniqueIds.length === 0) return;

    const fetchAll = async () => {
      const results: Record<number, IEsimPackage> = {};
      await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const res = await getPackageById(id);
            if (res.data) {
              results[id] = res.data;
            }
          } catch {
            // skip — row will fall back to showing the ID
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setPackageMap((prev) => ({ ...prev, ...results }));
      }
    };

    void fetchAll();
  }, [response]);

  const handleDelete = (id: number) => {
    deleteVoucherCode(id)
      .then(() => {
        toast.success("Vaucheri u fshi me sukses.", {
          position: "top-right"
        });
        setResponse((prev) =>
          prev
            ? {
                ...prev,
                data: prev.data.filter((el: IVoucherCode) => el.id !== id)
              }
            : null
        );
      })
      .catch(() => toast.error("Ndodhi nje gabim gjate fshirjes."));
  };

  const renderRewardDetail = (row: IVoucherCode) => {
    if (row.rewardType === 2) {
      return row.creditAmount !== null && row.creditAmount !== undefined
        ? `${row.creditAmount} EUR`
        : "-";
    }
    if (row.packageTemplateId === null || row.packageTemplateId === undefined) return "-";

    const pkg = packageMap[row.packageTemplateId];
    if (!pkg) {
      return (
        <span className="text-gray-400 text-xs">
          Package #{row.packageTemplateId}
        </span>
      );
    }

    return (
      <div className="flex items-center gap-2">
        {pkg.imageUrl && (
          <img
            src={pkg.imageUrl}
            alt={pkg.name}
            className="w-5 h-5 rounded object-contain"
          />
        )}
        <div className="leading-tight">
          <div className="text-sm font-medium truncate max-w-[160px]">
            {pkg.name}
          </div>
          <div className="text-xs text-gray-500">
            {formatGB(pkg.totalGB, pkg.isUnlimited)} · {pkg.validityDays}d ·{" "}
            {pkg.cost} EUR
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {response?.data?.length ? (
          response.data.map((row: IVoucherCode, i: number) => (
            <Row
              key={i}
              code={
                <span className="font-mono font-semibold">{row.code}</span>
              }
              rewardType={rewardTypeLabel(row.rewardType)}
              usageMode={usageModeLabel(row.usageMode)}
              rewardDetail={renderRewardDetail(row)}
              lockedToEmail={row.lockedToEmail || "-"}
              expiresAtUtc={
                row.expiresAtUtc
                  ?
                    dayjs
                        .utc(row.expiresAtUtc)
                        .tz("Europe/Belgrade")
                        .format("DD MMM YYYY · HH:mm")
                  : "-"
              }
              isActive={
                <span
                  className={
                    row.isActive ? "text-green-600" : "text-red-600"
                  }
                >
                  {row.isActive ? "Po" : "Jo"}
                </span>
              }
              redemptionCount={row.redemptionCount}
              audit={
                <AuditAuthor
                  entityName={row.auditAuthor?.entityName}
                  entityId={row.auditAuthor?.entityId}
                  createdByName={row.auditAuthor?.createdByName}
                  createdById={row.auditAuthor?.createdById}
                  createdByLastName={row.auditAuthor?.createdByLastName}
                  updatedByName={row.auditAuthor?.updatedByName}
                  updatedById={row.auditAuthor?.updatedById}
                  updatedByLastName={row.auditAuthor?.updatedByLastName}
                  insertDate={row.auditAuthor?.insertDate}
                  updateDate={row.auditAuthor?.updateDate}
                />
              }
              actions={
                <span className="flex items-center">
                  <Can permissions="vouchers.vouchercode.update">
                    <Link
                      to={`/esim/voucher-codes/edit/${row.id}`}
                      state={{
                        crumbs: ["Esim", "Voucher Codes", "Modifiko vaucherin"],
                        page: pageIndex,
                        voucherData: row
                      }}
                    >
                      <IconBtn className="w-6 h-6">
                        <PencilSquareIcon className="w-4 h-4" />
                      </IconBtn>
                    </Link>
                  </Can>
                  <Can permissions="vouchers.vouchercode.delete">
                    <DeleteVoucherCode
                      handleDelete={() => handleDelete(row.id)}
                    />
                  </Can>
                </span>
              }
            />
          ))
        ) : (
          <></>
        )}
      </Table>
    </div>
  );
};

export default VoucherCodes;
